import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const ProcessPage = () => {
  useEffect(() => {
    document.title = '삼원테크 | 제작과정 - STS패널형·원통형·라이닝 물탱크 제조 공정';
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      '삼원테크 물탱크 제작과정. 상담·설계부터 자재 준비, 정밀 가공, 품질 검사, 현장 설치, A/S까지 체계적인 6단계 제조 공정으로 최고 품질을 보장합니다.'
    );
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/process#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/process`,
      "name": "삼원테크 물탱크 제작과정",
      "description": "상담·설계부터 자재 준비, 정밀 가공, 품질 검사, 현장 설치, A/S까지 체계적인 6단계 제조 공정.",
      "inLanguage": "ko-KR",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "홈", "item": import.meta.env.VITE_SITE_URL || 'https://example.com' },
          { "@type": "ListItem", "position": 2, "name": "제작과정", "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/process` }
        ]
      }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-process';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-process')?.remove(); };
  }, []);

  const processes = [
    {
      id: 1,
      title: 'STS패널형 물탱크',
      description: '조립식 구조로 현장 설치가 용이하며, 대용량 저수 시설에 최적화된 제품입니다.',
      icon: 'ri-layout-grid-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/2d576a2c5bd3ef0148d6945391d8aaab.png',
      link: '/process/sts-panel',
      features: ['모듈형 설계', '현장 조립', '대용량 가능', '맞춤 제작']
    },
    {
      id: 2,
      title: 'STS원통형 물탱크',
      description: '일체형 구조로 내구성이 뛰어나며, 중소규모 저수 시설에 적합한 제품입니다.',
      icon: 'ri-shape-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/a622e2ab3f4ddef9675dcd029ea73e5d.jpeg',
      link: '/process/sts-cylinder',
      features: ['일체형 제작', '고강도 용접', '정밀 가공', '품질 검사']
    },
    {
      id: 3,
      title: '라이닝 물탱크',
      description: '특수 코팅 처리로 부식 방지 및 수질 보호 기능이 강화된 제품입니다.',
      icon: 'ri-paint-brush-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/752fcbc80e86976d105c29f7c58f6f6b.png',
      link: '/process/lining',
      features: ['특수 코팅', '부식 방지', '수질 보호', '장기 보증']
    }
  ];

  const steps = [
    {
      number: '01',
      title: '상담 및 설계',
      description: '고객 요구사항 분석 및 최적의 설계안 제시',
      icon: 'ri-discuss-line'
    },
    {
      number: '02',
      title: '자재 준비',
      description: '고품질 스테인리스 스틸 및 부자재 준비',
      icon: 'ri-stack-line'
    },
    {
      number: '03',
      title: '제작 및 가공',
      description: '정밀 가공 및 용접을 통한 제품 제작',
      icon: 'ri-tools-line'
    },
    {
      number: '04',
      title: '품질 검사',
      description: '엄격한 품질 기준에 따른 검사 및 테스트',
      icon: 'ri-shield-check-line'
    },
    {
      number: '05',
      title: '현장 설치',
      description: '전문 기술진의 안전한 현장 설치',
      icon: 'ri-hammer-line'
    },
    {
      number: '06',
      title: 'A/S 지원',
      description: '설치 후 지속적인 유지보수 및 관리',
      icon: 'ri-customer-service-2-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 text-white py-24">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
                제작과정
              </h1>
              <p className="text-xl md:text-2xl text-teal-50 max-w-3xl mx-auto leading-relaxed">
                30년 노하우로 완성하는 최고 품질의 물탱크
              </p>
            </motion.div>
          </div>
        </section>

        {/* Process Overview */}
        <section className="py-20 bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                제작 프로세스
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                체계적인 제작 시스템과 철저한 품질 관리로 최상의 제품을 제공합니다
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-8 border border-teal-100 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="text-5xl font-bold text-teal-600/20">
                      {step.number}
                    </div>
                    <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center text-white text-2xl flex-shrink-0">
                      <i className={step.icon}></i>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Types */}
        <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                제품별 제작과정
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                각 제품의 특성에 맞는 전문화된 제작 공정을 확인하세요
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {processes.map((process, index) => (
                <motion.div
                  key={process.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 group"
                >
                  <div className="relative w-full h-72 overflow-hidden">
                    <img
                      src={process.image}
                      alt={process.title}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center text-teal-600 text-3xl mb-4 shadow-lg">
                        <i className={process.icon}></i>
                      </div>
                      <h3 className="text-3xl font-bold text-white">
                        {process.title}
                      </h3>
                    </div>
                  </div>
                  
                  <div className="p-8">
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {process.description}
                    </p>
                    
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">주요 특징</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {process.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-teal-500 text-base"></i>
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <Link
                      to={process.link}
                      className="block w-full bg-teal-600 hover:bg-teal-700 text-white text-center py-3.5 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-xl whitespace-nowrap cursor-pointer"
                    >
                      상세 제작과정 보기
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Quality Assurance */}
        <section className="py-20 bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 rounded-3xl p-12 md:p-16 text-white shadow-2xl">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-4xl md:text-5xl font-bold mb-6">
                    품질 보증
                  </h2>
                  <p className="text-xl text-teal-50 mb-8 leading-relaxed">
                    ISO 9001 인증을 받은 품질 관리 시스템으로 모든 제품의 우수한 품질을 보증합니다
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-medal-line text-2xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">ISO 9001 인증</h3>
                        <p className="text-teal-50">국제 품질 경영 시스템 인증</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-check-line text-2xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">전수 검사</h3>
                        <p className="text-teal-50">모든 제품에 대한 철저한 품질 검사</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-customer-service-2-line text-2xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">A/S 보증</h3>
                        <p className="text-teal-50">설치 후 지속적인 유지보수 지원</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
                    <div className="text-5xl font-bold mb-2">30+</div>
                    <div className="text-teal-50">년 경력</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
                    <div className="text-5xl font-bold mb-2">5,000+</div>
                    <div className="text-teal-50">시공 실적</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
                    <div className="text-5xl font-bold mb-2">100%</div>
                    <div className="text-teal-50">품질 검사</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
                    <div className="text-5xl font-bold mb-2">24/7</div>
                    <div className="text-teal-50">A/S 지원</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-teal-50 via-white to-teal-50">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              제작 상담이 필요하신가요?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              전문 상담원이 고객님의 요구사항에 맞는 최적의 솔루션을 제안해드립니다
            </p>
            <Link
              to="/contact"
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              상담 문의하기
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProcessPage;